import type { AppKey } from '@/features/landing-page/data/app-detail'
import type { VideoThumbnail } from '@/components/inline-video'
import type { Locale } from '@/i18n/config'
import { SHOWCASE_APPS } from './app-showcase'

/** Any app can back a feature page. The per-category allow-lists below gate
    which keys a given route accepts. */
export type FeatureKey = AppKey

/** People Security feature pages: 1:1 with the showcased apps. */
export function isFeatureKey(v: string): v is FeatureKey {
  return (SHOWCASE_APPS as readonly string[]).includes(v)
}

/** Property Security feature pages. One app today (compliassist); the route
    allow-list stays explicit so a bad slug 404s. */
export const PROPERTY_FEATURES = [
  'compliassist',
] as const satisfies readonly AppKey[]

export function isPropertyFeatureKey(v: string): v is FeatureKey {
  return (PROPERTY_FEATURES as readonly string[]).includes(v)
}

/** Feature pages grouped by product category, people first (the order of the
    people-security page). Shared by the header menu and the footer; each `to`
    is the literal route id the `$feature` slug plugs into. */
export const FEATURE_GROUPS = [
  {
    key: 'people',
    to: '/$locale/solutions/people-security/$feature',
    features: SHOWCASE_APPS,
  },
  {
    key: 'property',
    to: '/$locale/solutions/property-security/$feature',
    features: PROPERTY_FEATURES,
  },
] as const

/** Motion-design video shown under a feature page's hero. The copy is burned
    into the frames, so `locales` lists the pages whose language matches it.
    `src` is the 1080p desktop cut, `srcMobile` the 720p one served under the
    `md` breakpoint; `thumbnail` is a still frame from the video. */
export type FeatureVideo = {
  src: string
  srcMobile: string
  thumbnail: VideoThumbnail
  locales: readonly Locale[]
}

/** Also shown in the landing hero, on every locale. */
export const COMPLIASSIST_VIDEO: FeatureVideo = {
  src: '/videos/compliassist-motion-1080.mp4',
  srcMobile: '/videos/compliassist-motion-720.mp4',
  thumbnail: {
    src: '/videos/thumbnails/compliassist-motion.webp',
    width: 1920,
    height: 1080,
  },
  locales: ['fr'],
}

export const FEATURE_VIDEOS: Partial<Record<FeatureKey, FeatureVideo>> = {
  compliassist: COMPLIASSIST_VIDEO,
  evac: {
    src: '/videos/evac-motion-1080.mp4',
    srcMobile: '/videos/evac-motion-720.mp4',
    thumbnail: {
      src: '/videos/thumbnails/evac-motion.webp',
      width: 1920,
      height: 1080,
    },
    locales: ['fr'],
  },
}
